/* Leads — website enquiries from the vault entrance, so nothing goes cold. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PhoneCall, Mail, Globe, Building2, CalendarClock, Save } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const TINT = {
  new: "border-primary-orange text-primary-orange",
  contacted: "border-sky-500 text-sky-400",
  qualified: "border-blue-500 text-blue-400",
  won: "border-emerald-500 text-emerald-400",
  lost: "border-industrial text-muted-industrial",
};

function LeadCard({ lead, onSaved }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [followUp, setFollowUp] = useState(lead.follow_up || "");
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, body);
      onSaved(data);
      toast.success("Lead updated");
    } catch {
      toast.error("Could not update that lead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-industrial p-4" data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-tag">{new Date(lead.created_at).toLocaleString()} · {lead.source}</div>
          <div className="heading text-lg font-black uppercase truncate">{lead.company || lead.name}</div>
          <div className="mono text-[11px] text-muted-industrial truncate">{lead.name}</div>
        </div>
        <div className="flex items-center gap-1 ml-auto flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} type="button" disabled={busy} onClick={() => patch({ status: s })}
              data-testid={`lead-status-${s}-${lead.id}`}
              className={`mono text-[9px] uppercase tracking-widest px-2 py-1 border ${
                lead.status === s ? `${TINT[s]} font-black` : "border-industrial text-muted-industrial hover:text-ink"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
        <a href={`tel:${lead.phone}`} data-testid={`lead-call-${lead.id}`}
          className="border border-industrial px-2.5 py-2 hover:border-primary-orange transition-colors">
          <div className="label-tag flex items-center gap-1"><PhoneCall className="w-3 h-3" /> Phone</div>
          <div className="mono text-sm font-bold">{lead.phone}</div>
        </a>
        <div className="border border-industrial px-2.5 py-2">
          <div className="label-tag flex items-center gap-1"><Mail className="w-3 h-3" /> Email</div>
          <div className="mono text-[11px] truncate">{lead.email || "—"}</div>
        </div>
        <div className="border border-industrial px-2.5 py-2">
          <div className="label-tag flex items-center gap-1"><Globe className="w-3 h-3" /> Country</div>
          <div className="mono text-[11px]">{lead.country || "—"}</div>
        </div>
        <div className="border border-industrial px-2.5 py-2">
          <div className="label-tag flex items-center gap-1"><Building2 className="w-3 h-3" /> Company</div>
          <div className="mono text-[11px] truncate">{lead.company || "—"}</div>
        </div>
      </div>

      {lead.message && (
        <div className="mt-3 border-l-2 border-primary-orange pl-3 text-sm text-muted-industrial whitespace-pre-wrap">{lead.message}</div>
      )}

      <div className="grid sm:grid-cols-[1fr_180px] gap-2 mt-3 items-start">
        <div>
          <div className="label-tag">Notes</div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="Called 9am — wants a demo Thursday"
            data-testid={`lead-notes-${lead.id}`}
            className="rounded-none bg-transparent border-industrial text-sm mt-1" />
        </div>
        <div>
          <div className="label-tag flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Follow up</div>
          <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)}
            data-testid={`lead-followup-${lead.id}`}
            className="rounded-none bg-transparent border-industrial mono text-xs h-9 mt-1" />
          <button type="button" disabled={busy} onClick={() => patch({ notes, follow_up: followUp })}
            data-testid={`lead-save-${lead.id}`}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 mono text-[10px] uppercase tracking-widest font-black px-3 py-2 bg-primary-orange text-black disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: leads.length };
    STATUSES.forEach((s) => { c[s] = leads.filter((l) => (l.status || "new") === s).length; });
    return c;
  }, [leads]);
  const shown = filter === "all" ? leads : leads.filter((l) => (l.status || "new") === filter);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="leads-page">
      <div className="flex items-end gap-4 flex-wrap">
        <div>
          <div className="label-tag">Sales pipeline</div>
          <h1 className="heading text-3xl font-black uppercase">Leads</h1>
        </div>
        <div className="mono text-[11px] uppercase tracking-widest text-muted-industrial ml-auto">
          Enquiries from the website vault · newest first
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button key={s} type="button" onClick={() => setFilter(s)} data-testid={`leads-filter-${s}`}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${
              filter === s ? "bg-primary-orange border-primary-orange text-black font-black" : "border-industrial text-muted-industrial hover:text-ink"}`}>
            {s} {counts[s] ? `· ${counts[s]}` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card-industrial p-8 text-center mono text-xs uppercase tracking-widest text-muted-industrial">Loading leads…</div>
      ) : shown.length === 0 ? (
        <div className="card-industrial p-10 text-center" data-testid="leads-empty">
          <div className="heading text-lg font-black uppercase">No leads yet</div>
          <div className="mono text-[11px] uppercase tracking-widest text-muted-industrial mt-2">
            Share armorbatch.com — every "call me back" lands here
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((l) => (
            <LeadCard key={l.id} lead={l} onSaved={(u) => setLeads((p) => p.map((x) => (x.id === u.id ? u : x)))} />
          ))}
        </div>
      )}
    </div>
  );
}
